import { Injectable } from '@angular/core';
import { DateService } from '../../../date/date.service';
import { DateParserService } from '../../../daily-log/daily-log-time/date-parser-service';
import { OperationService } from '../../operation/operation.service';
import {
  DailyLogOperationInstance,
  GapDetector
} from '../../../../models/projects/operation.models';
import { ErrorType, StartAndEnd, ValidationResult } from '../../../../models/projects/validation.models';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { TimeZoneService } from '../../../time-zone/time-zone.service';
import { DateTime } from 'luxon';

@Injectable()
export class ConcurrentOperationValidatorService {

  constructor(
    private dateService: DateService,
    private dateParserService: DateParserService,
    private operationService: OperationService,
    private timeZoneService: TimeZoneService
  ) {
  }

  async findGapDuringTimeSpan(startTime: PtLuxonDate, endTime: PtLuxonDate, validationResult: ValidationResult, dailyLogDate: DateTime, currentInstanceId: number) {

    if (!startTime || !endTime) {
      return;
    }

    let allOperations = await this.operationService.getAllProjectSpecificOperationInstances();

    // remove current operation from the list - so gap validation will still appear when editing a contractual op and switching it to be a concurrent one
    const currentInstanceIndex = allOperations.findIndex(e => e.id ? e.id === currentInstanceId : e.localId === currentInstanceId);
    if (currentInstanceIndex !== null && currentInstanceIndex !== undefined && currentInstanceIndex !== -1) {
      allOperations.splice(currentInstanceIndex, 1);
    }

    // set op end time to end of today if null
    for (let i = 0; i < allOperations.length; i++) {
      if (!allOperations[i].endTime || !allOperations[i].endTime.dateTime) {
        allOperations[i].endTime = await this.dateService.getEndOfDayLocally(dailyLogDate);
      }
    }

    const relevantOperations = allOperations
      .filter(p => {
        let found = this.operationService.operationInTime(
          startTime.dateTime,
          endTime.dateTime,
          p.startTime.dateTime,
          p.endTime.dateTime);

        if (!found) {
          found = this.operationService.operationInTime(
            p.startTime.dateTime,
            p.endTime.dateTime,
            startTime.dateTime,
            endTime.dateTime);
        }

        return found;
      })
      .filter(p => p.startTime !== p.endTime)
      .filter(p => !p.isConcurrentOperation);

    let gapDetector = new GapDetector();
    gapDetector.currentTime = startTime;

    const gaps = [];

    while (!gapDetector.finished) {
      gapDetector = this.findNextViableTime(relevantOperations, gapDetector.currentTime, endTime);
      if (gapDetector.gapFound === true) {
        const gap = new StartAndEnd();
        gap.startTime = gapDetector.currentTime;

        if (!!gapDetector.operation) {
          gap.operationInstance.projectSpecificOperation = gapDetector.operation.projectSpecificOperation;
          gap.operationInstanceId = gapDetector.operation.id;
        }

        const newEndTime = this.findNextTime(relevantOperations, gapDetector.currentTime);

        if (!newEndTime) {
          gap.endTime = endTime;
          gapDetector.finished = true;
        } else {
          gap.endTime = newEndTime;
        }

        gaps.push(gap);

        gapDetector.currentTime = gap.endTime;
      } else {
        gapDetector.finished = true;
      }
    }

    for (const gap of gaps) {
      if (endTime.dateTime != null) {
        validationResult.addError(`You are adding this concurrent operation from
      ${this.dateService.setTimeStringFromPtLuxonDate(startTime)} to
      ${this.dateService.setTimeStringFromPtLuxonDate(endTime)} even though there is no contractual operation from
      ${this.dateService.setTimeStringFromPtLuxonDate(gap.startTime)} to
      ${this.dateService.setTimeStringFromPtLuxonDate(gap.endTime)}; this is not advisable.`, ErrorType.Warning);
      } else {
        validationResult.addError(`You are adding this open ended concurrent operation starting from
      ${this.dateService.setTimeStringFromPtLuxonDate(startTime)}, this will result in there being times where no contractual operations
        are running at the same time, which is not advisable.`, ErrorType.Warning);
      }
    }
  }

  findNextViableTime(relevantOperations: DailyLogOperationInstance[], currentTime: PtLuxonDate, endTime: PtLuxonDate): GapDetector {
    const gapDetector = new GapDetector();
    gapDetector.gapFound = true;
    gapDetector.finished = false;
    gapDetector.currentTime = currentTime;

    for (let i = 0; i < relevantOperations.length; i++) {

      if (gapDetector.currentTime.dateTime >= relevantOperations[i].startTime.dateTime
        && gapDetector.currentTime.dateTime <= relevantOperations[i].endTime.dateTime) {
        gapDetector.currentTime = relevantOperations[i].endTime;
        gapDetector.operation = relevantOperations[i];
        gapDetector.gapFound = false;
      }

      if (gapDetector.currentTime.dateTime >= endTime.dateTime) {
        gapDetector.finished = true;
        return gapDetector;
      }
    }

    return gapDetector;
  }

  findNextTime(relevantOperations: DailyLogOperationInstance[], time: PtLuxonDate): PtLuxonDate {
    const laterOperations = relevantOperations.filter(p => p.startTime.dateTime > time.dateTime);

    if (laterOperations.length < 1) {
      return null;
    }

    const options = laterOperations
      .sort(p => p.startTime.dateTime.toMillis())
      .map(p => p.startTime);

    const nextTime = options[options.length - 1];

    return nextTime;
  }


}



// WEBPACK FOOTER //
// ./src/services/operations/operation-validation/concurrent-operation-validator-service/concurrent-operation-validator.service.ts